/* eslint-disable no-underscore-dangle */
import { EventEmitter } from 'events';
import uniq from 'lodash/uniq';
import isFunction from 'lodash/isFunction';
import each from 'lodash/each';
import clone from 'lodash/clone';
import MixPanel from '@haptik/helpers/build/esm/MixPanel';

import { register } from './dispatcher';
import { CHANGE, ENVIRONMENT, PERMISSION_LEVELS } from './const';
import { updateState } from './utils';
import { ActionTypes } from './constants/MogamboConstants';

// TEMP: Mixpanel events quota exhausted, block all events until quota resets.
const BLOCK_MIXPANEL_EVENTS = true;

export function createStore(spec) {
  const emitter = new EventEmitter();
  emitter.setMaxListeners(1000);

  const store = {
    emitChange() {
      emitter.emit(CHANGE);
    },

    emit(eventName) {
      emitter.emit(eventName);
    },

    addChangeListener(callback) {
      emitter.on(CHANGE, callback);
    },

    removeChangeListener(callback) {
      emitter.removeListener(CHANGE, callback);
    },

    addSingleChangeListener(constant, callback) {
      emitter.on(constant, callback);
    },

    removeSingleChangeListener(constant, callback) {
      emitter.removeListener(constant, callback);
    },
    ...spec,
  };

  // Auto-bind store methods for convenience
  each(store, (val, key) => {
    if (isFunction(val)) {
      store[key] = store[key].bind(store);
    }
  });

  return store;
}

const BotStore = createStore({
  _location: {
    currentLocation: null,
    prevLocation: null,
  },
  _bot: null,
  _screen: null,
  _allBots: [],
  _allStories: [],
  _allStoriesMeta: [],
  _generalNodes: [],
  _editedGeneralNodes: [],
  _generalResponses: [],
  _editedGeneralResponses: {},
  _stepPriorities: {},
  _editedStepPriorities: {},
  _meanings: {},
  _updatedSynonyms: [],
  _botResponses: {},
  _permissions: [],
  _allSteps: [],
  _indexedColors: {},
  _synonyms: [],
  _allLanguages: [],
  _authCode: '',
  _features: {},
  _cas: '',
  _enableSmartSkills: true,
  _userPartnerList: [],
  _enableNodeContextMenu: true,
  _mixPanel: '',
  _selfServe: false, // Meant for IVAs via Feature Toggles
  _selfServeLayout: true, // Meant for global components like Topbar, Sidebar, etc.
  _headerProps: {}, // For passing existing header props in new header
  _RHSActive: false,
  _selfServeUser: false,
  _topbarEvents: {},
  _testConfigData: {},
  _testConfigErr: '',
  _selectedBotBusinesses: [],
  _isSmartSkillSelectedViaPage: false,
  _corsEnabled: null,
  _whatsappPayEnabled: false,
  _entityV2Enabled: false,
  _allPlatformsData: {},
  zohoTokens: null,

  // Partner List recieved from get_user_info
  _userInfoPartnerList: [],

  setUserInfo(data) {
    if (data.env != '') {
      this._env = data.env;
    }

    if (data.cas !== '') {
      this._cas = data.cas;
    }

    if (data.username != '') {
      this._username = data.username;
      this._userFullName = data.fullname || data.username;
    }

    if (data.useremail != '') {
      this._useremail = data.useremail;
    }

    if (data.authCode != '') {
      this._authCode = data.authCode || '';
    }

    if (data.partners) {
      this._userInfoPartnerList = data.partners;
    }

    this._supermanSupport = data.superman_support || false;

    if (data.permissions != '') {
      this._permissions = data.permissions || [];
    }

    this._selfServeUser = data.is_self_serve_user || false;
  },

  getUserData() {
    const userInfo = {
      fullname: this._userFullName,
      username: this._username,
      useremail: this._useremail,
      authcode: this._authCode,
      permissions: this._permissions,
      userInfoPartnerList: this._userInfoPartnerList,
    };
    return userInfo;
  },

  getSelfServeUser() {
    return this._selfServeUser;
  },

  setTopbarEvents(events) {
    this._topbarEvents = events;
  },

  getTopbarEvents() {
    return this._topbarEvents;
  },

  setTestConfigData(data) {
    this._testConfigData = data;
  },

  getTestConfigData() {
    return this._testConfigData;
  },

  setTestConfigErr(err) {
    this._testConfigErr = err;
  },

  getTestConfigErr() {
    return this._testConfigErr;
  },

  setSelfServeLayout(flag) {
    this._selfServeLayout = flag;
  },

  getSelfServeLayout() {
    return this._selfServeLayout;
  },

  setFeaturesData(data) {
    this._features = data;
  },

  getFeaturesData() {
    return this._features;
  },

  getFeaturesDataForProM() {
    if (!this._features) {
      return {};
    }
    const { enable_sms_prom, disable_whatsapp_prom } = this._features;
    return { enable_sms_prom, disable_whatsapp_prom };
  },

  setUserPartnersList(data) {
    this._userPartnerList = data;
  },

  getUserPartnersList() {
    return this._userPartnerList;
  },

  getWhatsappPay() {
    return this._whatsappPayEnabled;
  },

  setWhatsappPay(data) {
    this._whatsappPayEnabled = data;
  },

  resetData() {
    this._allBots = [];
    this._allStories = [];
    this._allStoriesMeta = [];
    this._formEntities = [];
    this._allFormEntities = [];
    this._stepPriorities = {};
    this._botResponses = {};
    this._synonyms = {};
    this._updatedSynonyms = {};
  },

  setAllBots(domainData, domainMetadata) {
    domainData.map((domain) => {
      const businessNames =
        domainMetadata.business_domain_map[domain.title] || [];
      domain.businesses = businessNames.map((businessId) => {
        const business = domainMetadata.business_partner_map[businessId] || {};
        return {
          name: business.name,
          partners: business.partners,
        };
      });
    });

    this._allBots = domainData;
    this._allLanguages = domainMetadata.all_languages;
    this.sortBotList();
  },

  getAllBots() {
    return this._allBots;
  },

  getAllLanguages() {
    return this._allLanguages;
  },

  addNewBot(data) {
    this._allBots.push(data);
    this.sortBotList();
  },

  updateBot(botId, data) {
    this._allBots.forEach((bot) => {
      if (bot.id == botId) {
        bot.client = data.client;
        bot.description = data.description;
        bot.active = data.active;
        bot.test_business = data.test_business;
      }
    });
  },

  sortBotList() {
    this._allBots.sort((bot1, bot2) => {
      return bot1.title.toLowerCase() > bot2.title.toLowerCase() ? 1 : -1;
    });
  },

  setAllStories(data) {
    this._allStories = data;
    this.sortStoryList();
  },

  setAllStoriesMeta(data) {
    this._allStoriesMeta = data;
  },

  getAllStoriesMeta() {
    return this._allStoriesMeta;
  },

  getAllStories() {
    return this._allStories;
  },

  sortStoryList() {
    this._allStories.sort((story1, story2) => {
      return story1.title.toLowerCase() > story2.title.toLowerCase() ? 1 : -1;
    });
  },

  createTempStory(data) {
    this._allStories.push(data);
    this.sortStoryList();
  },

  updateStory(data) {
    const stories = this._allStories;
    // eslint-disable-next-line no-restricted-syntax
    for (const id in stories) {
      if (stories[id].title.toLowerCase() == data.title.toLowerCase()) {
        stories.splice(id, 1, data);
        break;
      }
    }
    this._allStories = stories;
  },

  setStepPriorities(data) {
    data.priority_list = data.priority_list.map((_step) => {
      _step.active = true;
      return _step;
    });

    this._stepPriorities = data;
  },

  getStepPriorities() {
    return this._stepPriorities;
  },

  updateStepPriorities(data, args) {
    if (!this._stepPriorities.priority_list) {
      this._stepPriorities.priority_list = [];
    }

    const update = updateState(
      data,
      this._stepPriorities.priority_list,
      this._editedStepPriorities,
      'step-priorities',
      args
    );

    this._stepPriorities.priority_list = update.data;
    this._editedStepPriorities = update.edited;

    if (update.update) {
      BotStore.emitChange();
    }
  },

  setGeneralResponses(data) {
    this._generalResponses = data.responses;
  },

  getGeneralResponses() {
    return this._generalResponses;
  },

  getEditedGeneralResponses() {
    return this._editedGeneralResponses;
  },

  updateGeneralResponses(data) {
    const update = updateState(
      data,
      this._generalResponses,
      this._editedGeneralResponses,
      'general-responses'
    );

    this._generalResponses = update.data;
    this._editedGeneralResponses = update.edited;
    this._editedGeneralResponses.domainId = data.context;
  },

  setGeneralNodes(data) {
    this._generalNodes = data.nodes;
    if (data.synonyms) {
      this.setSynonyms(data.synonyms || []);
    }
    if (data.nodes) {
      data.nodes.forEach((node) => {
        node.user_responses = node.user_responses.sort((a, b) => {
          if (a.active === b.active) return 0;
          if (a.active) return -1;
          if (b.active) return 1;
          if (a.sentence == '') return 1;
          if (b.sentence == '') return -1;
          return 0;
        });
      });
    }
  },

  updateGeneralNodes(success, failure) {
    /*
    success => Data that has been successfully updated
    failure => Data that couldn't be saved successfully.

    To allow the user to update/fix the failed data, we mark it as edited data
    for the components to render accordingly.
    */
    const generalNodes = clone(this._generalNodes);

    success.forEach((successNode) => {
      let found = false;
      // eslint-disable-next-line no-restricted-syntax
      for (const index in generalNodes) {
        // If existing node => node_id will match
        if (generalNodes[index].node_id == successNode.node_id) {
          generalNodes[index] = successNode;
          found = true;
        }
      }

      // Not found => New Node that was saved successfully.
      if (!found) {
        generalNodes.push(successNode);
      }
    });

    this._generalNodes = generalNodes;

    // Failed data to be shown as edited/unsaved changes
    this._editedGeneralNodes = failure;
  },

  getGeneralNodes() {
    return this._generalNodes;
  },

  getEditedGeneralNodes() {
    return this._editedGeneralNodes;
  },

  setLocation(data) {
    this._location.prevLocation = this._location.currentLocation;
    this._location.currentLocation = data.pathname;
  },

  getLocation() {
    return this._location;
  },

  getEnvironment() {
    return this._env;
  },

  isProductionEnv() {
    return this._env === ENVIRONMENT.PRODUCTION;
  },

  getCAS() {
    return this._cas;
  },

  getUsername() {
    return this._username;
  },

  getUserEmail() {
    return this._useremail;
  },

  getAuthCode() {
    return this._authCode;
  },

  getSupermanSupport() {
    return this._supermanSupport;
  },

  getPermissions() {
    return this._permissions;
  },

  canViewEditSmartSkill() {
    return this.getPermissions().includes(
      PERMISSION_LEVELS.SMART_SKILLS_VIEW_EDIT
    );
  },

  getSynonyms() {
    return this._synonyms;
  },

  getEditedSynonyms() {
    return this._updatedSynonyms;
  },

  setSynonyms(synonyms) {
    this._synonyms = Array.isArray(this._synonyms) ? this._synonyms : [];
    this._synonyms = uniq(this._synonyms.concat(synonyms), (synonym) =>
      synonym.source_word.toString()
    );
  },

  setSmartSkillsFlag(enableSmartSkills) {
    this._enableSmartSkills = enableSmartSkills;
  },

  getSmartSkillsFlag() {
    return this._enableSmartSkills;
  },

  getEnableNodeContextMenuFlag() {
    return this._enableNodeContextMenu;
  },

  getIsSelfServe() {
    return this._selfServe;
  },

  setIsSelfServe(isSelfServe) {
    this._selfServe = isSelfServe;
  },

  getBusinessesLinkedToBot() {
    return this._selectedBotBusinesses;
  },

  setBusinessesLinkedToBot(businesses) {
    this._selectedBotBusinesses = businesses;
  },

  setCorsEnableFlag(flag) {
    this._corsEnabled = flag;
  },

  getIsCorsEnabled() {
    return this._corsEnabled;
  },

  // eslint-disable-next-line camelcase
  setMixPanel({ cas, env, username, useremail, is_self_serve_user }) {
    this._mixPanel = new MixPanel({
      CAS: cas,
      'Tool Name': 'Conversation Studio',
      Environment: env,
      'Self-Serve User': is_self_serve_user,
    });

    this._mixPanel.setUser({
      email: useremail,
      id: username,
    });

    if (typeof window !== 'undefined') {
      window.mixpanel = this._mixPanel.mixpanel;
    }
  },

  getMixPanel() {
    // TEMP: Mixpanel events quota exhausted, block all events until quota resets.
    // Remove this block to restore normal tracking.
    if (BLOCK_MIXPANEL_EVENTS) {
      return { track: () => {} };
    }
    return this._mixPanel;
  },

  setHeaderProps(props) {
    this._headerProps = { ...props };
  },

  getHeaderProps() {
    return this._headerProps;
  },

  setRHSActive(flag) {
    this._RHSActive = flag;
  },

  getRHSActive() {
    return this._RHSActive;
  },

  setIsSmartSkillSelectedViaPage(flag) {
    this._isSmartSkillSelectedViaPage = flag;
  },

  getIsSmartSkillSelectedViaPage() {
    return this._isSmartSkillSelectedViaPage;
  },

  getIsEntityV2Enabled() {
    return this._entityV2Enabled;
  },

  setIsEntityV2Enabled(flag) {
    this._entityV2Enabled = flag;
  },

  setPlatformsData(data) {
    this._allPlatformsData = data;
  },

  getAllPlatformsData() {
    return this._allPlatformsData;
  },

  setZohoTokens(tokens) {
    this.zohoTokens = tokens;
    BotStore.emitChange();
  },

  getZohoTokens() {
    return this.zohoTokens;
  },

  setFeatureFlag(feature, flag) {
    this[feature] = flag;
  },

  getFeatureFlag(feature) {
    return this[feature];
  },

  setConstantDrawerRef(ref) {
    this.globalConstantDrawerRef = ref;
  },

  getConstantDrawerRef() {
    return this.globalConstantDrawerRef;
  },

  setDuplicateModalActiveFlag(flag) {
    this._duplicateModalActiveFlag = flag;
  },

  getDuplicateModalActiveFlag() {
    return this._duplicateModalActiveFlag;
  },
});

BotStore.dispatchToken = register((payload) => {
  const actionType = payload.type;
  const { action } = payload;
  switch (actionType) {
    case ActionTypes.RECEIVE_ALL_BOTS:
      BotStore.setAllBots(action.domainData, action.domainMetadata);
      BotStore.emitChange();
      if (action.callback) action.callback();
      break;

    case 'RECEIVE_NEW_BOT':
      BotStore.addNewBot(action.data);
      BotStore.emitChange();
      break;

    case 'CREATE_NEW_STORY':
      BotStore.createTempStory(action.data);
      BotStore.emitChange();
      break;

    case 'RECEIVE_NEW_STORY':
      BotStore.updateStory(action.data);
      BotStore.emitChange();
      break;

    case 'GET_ALL_STORIES':
      BotStore.emitChange();
      break;

    case ActionTypes.RECEIVE_ALL_STORIES_SUCCESS:
      BotStore.setAllStories(action.data);
      BotStore.setAllStoriesMeta(action.meta);
      BotStore.emitChange();
      break;

    case 'RECEIVE_STEP_PRIORITIES':
      BotStore.setStepPriorities(action.data);
      BotStore.emitChange();
      break;

    case 'UPDATE_STEP_PRIORITIES':
      BotStore.updateStepPriorities(action.data, action.args);
      break;

    case 'RECEIVE_GENERAL_RESPONSES':
      BotStore.setGeneralResponses(action.data);
      BotStore.emitChange();
      break;

    case 'UPDATE_GENERAL_RESPONSES':
      BotStore.updateGeneralResponses(action.data);
      BotStore.emitChange();
      break;

    case 'LOCATION_CHANGED':
      BotStore.setLocation(action.data);
      BotStore.emitChange();
      break;

    case ActionTypes.SET_GENERAL_NODES:
      BotStore.setGeneralNodes(action.data);
      BotStore.emitChange();
      break;

    case ActionTypes.UPDATE_GENERAL_NODES:
      BotStore.updateGeneralNodes(action.success, action.failure);
      BotStore.emitChange();
      break;

    case 'GET_UPDATES':
      BotStore.emitChange();
      break;

    case 'ZOHO_TOKENS':
      BotStore.setZohoTokens(action.data);
      break;

    case ActionTypes.SET_RHS_ACTIVE:
      BotStore.setRHSActive(action.flag);
      BotStore.emitChange();
      break;

    case ActionTypes.SET_GLOBAL_CONSTANT_DRAWER_REF:
      BotStore.setConstantDrawerRef(action.ref);
      BotStore.emitChange();
      break;

    default:
      break;
  }
});

export default BotStore;
